import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '../utils/api';
import { useSiteContent } from '../hooks/useSiteContent';
import { useAuth } from '../contexts/AuthContext';
import HamburgerHintOverlay from '../components/HamburgerHintOverlay';

// Shapes match backend/src/routes/marketingContent.ts + prisma schema.
interface Testimonial {
  id: string;
  quote: string;
  attribution: string;
  context?: string | null;
}

interface Outcome {
  id: string;
  studentLabel: string;
  result: string;
  subject?: string | null;
}

interface FaqRow {
  id: string;
  question: string;
  answer: string;
}

// Fallback FAQ rendered when /api/faq returns zero rows. Keeps the section
// meaningful on a fresh install. Copy follows the EJL voice rules (sentence
// case questions, no em dashes, no banned words, parent-leaning tone).
const FALLBACK_FAQ: FaqRow[] = [
  {
    id: 'fallback-1',
    question: 'Do you teach online, in person, or both?',
    answer: "Both. Online sessions run over video call and work well for anywhere in the UK. In-person sessions are available within London Zones 1 to 4.",
  },
  {
    id: 'fallback-2',
    question: 'How long are sessions and how often do they run?',
    answer: "Most sessions are 60 minutes and run weekly. For 11+ students closer to exam dates we sometimes move to 90 minutes or twice a week. We agree the schedule after the intro call.",
  },
  {
    id: 'fallback-3',
    question: 'What happens in the 20-minute intro call?',
    answer: "We talk about what your child is working on, where they're finding things hard, and what you want them to get out of tutoring. If I'm not the right fit I'll say so.",
  },
  {
    id: 'fallback-4',
    question: 'How are payments handled?',
    answer: "Invoiced monthly for the sessions that ran. Prepaid hour packages are also available at a small discount for students committing to weekly sessions across a term.",
  },
  {
    id: 'fallback-5',
    question: "What's your cancellation policy?",
    answer: "Any session can be cancelled or moved with 24 hours' notice at no charge. Later than that the session is charged at the usual rate.",
  },
  {
    id: 'fallback-6',
    question: 'Are you DBS-checked?',
    answer: "Yes. I hold an enhanced DBS check and can share the certificate on request before the first session. Parents are welcome to sit in on any session.",
  },
];

const HomePage: React.FC = () => {
  const c = useSiteContent();
  const { user } = useAuth();

  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const [faq, setFaq] = useState<FaqRow[]>([]);
  const [faqLoaded, setFaqLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/testimonials').then(res => {
      if (!cancelled) setTestimonials(Array.isArray(res.data) ? res.data : []);
    }).catch(() => { if (!cancelled) setTestimonials([]); });
    api.get('/outcomes').then(res => {
      if (!cancelled) setOutcomes(Array.isArray(res.data) ? res.data : []);
    }).catch(() => { if (!cancelled) setOutcomes([]); });
    api.get('/faq').then(res => {
      if (!cancelled) {
        setFaq(Array.isArray(res.data) ? res.data : []);
        setFaqLoaded(true);
      }
    }).catch(() => { if (!cancelled) { setFaq([]); setFaqLoaded(true); } });
    return () => { cancelled = true; };
  }, []);

  // Single-open accordion: keep refs to each <details> and close the others
  // when one opens. Uses DOM-native open state so JS stays minimal.
  const detailsRefs = useRef<(HTMLDetailsElement | null)[]>([]);
  const handleToggle = (idx: number) => (e: React.SyntheticEvent<HTMLDetailsElement>) => {
    if ((e.currentTarget as HTMLDetailsElement).open) {
      detailsRefs.current.forEach((el, i) => {
        if (el && i !== idx) el.open = false;
      });
    }
  };

  const faqItems = faqLoaded && faq.length === 0 ? FALLBACK_FAQ : faq;

  return (
    <div className="bg-cream">
      <HamburgerHintOverlay />
      <Helmet>
        <title>EJL Education — One-to-one tutoring for 11+, GCSE, A-level and Oxbridge</title>
        <meta name="description" content="One-to-one tutoring from an Oxford PPE offer-holder. £5 20-minute intro call." />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'EducationalOrganization',
          name: 'EJL Education',
          url: 'https://ejleducation.com',
          description: 'One-to-one tutoring for 11+, GCSE, A-level and Oxbridge applicants.',
          founder: { '@type': 'Person', name: 'Ethan Langley' },
          areaServed: 'London, United Kingdom',
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FALLBACK_FAQ.map((f) => ({
            '@type': 'Question',
            name: f.question,
            acceptedAnswer: { '@type': 'Answer', text: f.answer },
          })),
        })}</script>
      </Helmet>
      {/* ─── Hero ──────────────────────────────────────────────────────── */}
      <section className="section pt-24 md:pt-32">
        <div className="container-content">
          <p className="eyebrow mb-6">
            {c('hero_eyebrow', '11+, GCSE, A-LEVEL AND OXBRIDGE TUTORING')}
          </p>
          <h1 className="max-w-[18ch]">
            {c('hero_h1', 'One-to-one tutoring from an Oxford PPE offer-holder.')}
          </h1>
          <p className="mt-6 max-w-[62ch] text-body text-ink/90">
            {c('hero_sub', "I'm Ethan, a Year 13 at Wilson's School heading to Hertford College, Oxford. I tutor 11+, GCSE, A-level and Oxbridge applicants online and in London.")}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Link to="/contact" className="btn btn-primary">
              {c('hero_cta_primary', 'Book a £5 20-minute call')}
            </Link>
            <Link to="/subjects" className="btn btn-secondary">
              {c('hero_cta_secondary', 'See subjects and rates')}
            </Link>
            {!user && (
              <Link to="/register" className="btn btn-secondary">
                {c('hero_cta_register', 'Create an account')}
              </Link>
            )}
          </div>
          <p className="mt-10 text-[13px] text-muted leading-relaxed max-w-[70ch]">
            {c('hero_proof_row', '10 grade 9s at GCSE · DBS-checked · Hertford College, Oxford · Weekly instructor at TestTeach')}
          </p>
        </div>
      </section>

      {/* ─── Stats ─────────────────────────────────────────────────────── */}
      <section className="border-t border-rule">
        <div className="container-content py-12 md:py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10">
            <div>
              <div className="font-serif text-ochre text-[44px] md:text-[56px] leading-none tracking-tight">
                {c('stat_gcse', '10')}
              </div>
              <p className="mt-3 text-[13px] text-muted leading-snug">
                {c('stat_gcse_sub', 'grade 9s at GCSE')}
              </p>
            </div>
            <div>
              <div className="font-serif text-ochre text-[44px] md:text-[56px] leading-none tracking-tight">
                {c('stat_oxford', 'Hertford')}
              </div>
              <p className="mt-3 text-[13px] text-muted leading-snug">
                {c('stat_oxford_sub', 'College, Oxford (PPE offer)')}
              </p>
            </div>
            <div>
              <div className="font-serif text-ochre text-[44px] md:text-[56px] leading-none tracking-tight">
                {c('stat_debate', '3rd')}
              </div>
              <p className="mt-3 text-[13px] text-muted leading-snug">
                {c('stat_debate_sub', 'best speaker in Europe, England team')}
              </p>
            </div>
            <div>
              <div className="font-serif text-ochre text-[44px] md:text-[56px] leading-none tracking-tight">
                {c('stat_dbs', 'DBS')}
              </div>
              <p className="mt-3 text-[13px] text-muted leading-snug">
                {c('stat_dbs_sub', 'checked tutor')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── About (short) ─────────────────────────────────────────────── */}
      <section className="section border-t border-rule">
        <div className="container-content max-w-[70ch]">
          <h2>{c('home_about_h', 'About me')}</h2>
          <p className="mt-6 text-body text-ink/90">
            {c('home_about_body', "I'm Ethan. I'm in Year 13 at Wilson's School and I have an offer to read Philosophy, Politics and Economics at Hertford College, Oxford. I tutor 11+, GCSE, A-level and Oxbridge applicants in the subjects I know best.")}
          </p>
          <p className="mt-6">
            <Link to="/about" className="text-ochre">
              {c('home_about_cta', 'Read more about me')}
            </Link>
          </p>
        </div>
      </section>

      {/* ─── How a first session works ─────────────────────────────────── */}
      <section className="section border-t border-rule">
        <div className="container-content">
          <p className="eyebrow mb-4">Getting started</p>
          <h2 className="max-w-[28ch]">{c('how_heading', 'How a first session works')}</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-10 md:gap-8">
            <div>
              <div className="font-serif text-ochre text-[28px] leading-none mb-4">01</div>
              <div className="border-t border-rule pt-5">
                <h3 className="font-serif text-[20px] text-ink">
                  {c('how_step1_h', 'We talk first')}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-ink/90">
                  {c('how_step1_body', "Before any tutoring, we book a 20-minute call (£5) so I can understand what your child needs and tell you honestly whether I can help. No pressure either way.")}
                </p>
              </div>
            </div>
            <div>
              <div className="font-serif text-ochre text-[28px] leading-none mb-4">02</div>
              <div className="border-t border-rule pt-5">
                <h3 className="font-serif text-[20px] text-ink">
                  {c('how_step2_h', 'Diagnostic session')}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-ink/90">
                  {c('how_step2_body', "The first paid session is a diagnostic. I find out exactly where your child is, what they're getting wrong, and what the fastest path forward looks like. You get a written summary afterwards.")}
                </p>
              </div>
            </div>
            <div>
              <div className="font-serif text-ochre text-[28px] leading-none mb-4">03</div>
              <div className="border-t border-rule pt-5">
                <h3 className="font-serif text-[20px] text-ink">
                  {c('how_step3_h', 'Weekly sessions with a plan')}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-ink/90">
                  {c('how_step3_body', "Sessions run weekly by video call or in person in South London. Between sessions I set targeted practice and share a short write-up so you can see what's being worked on and where progress is happening.")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Testimonials ──────────────────────────────────────────────── */}
      <section className="section border-t border-rule">
        <div className="container-content">
          <h2>{c('testimonials_heading', 'What parents and students say')}</h2>
          <div className="mt-12 grid md:grid-cols-2 gap-10 md:gap-14">
            {testimonials.length === 0 ? (
              <p className="text-muted md:col-span-2">Testimonials coming soon.</p>
            ) : (
              testimonials.map(t => (
                <figure key={t.id}>
                  <blockquote className="font-serif text-[21px] leading-[1.45] text-ink">
                    &ldquo;{t.quote}&rdquo;
                  </blockquote>
                  <figcaption className="mt-4 text-sm text-muted">
                    {t.attribution}{t.context ? `, ${t.context}` : ''}
                  </figcaption>
                </figure>
              ))
            )}
          </div>
        </div>
      </section>

      {/* ─── Outcomes ──────────────────────────────────────────────────── */}
      {outcomes.length > 0 && (
        <section className="section border-t border-rule">
          <div className="container-content">
            <h2>{c('outcomes_heading', 'Where students have gone')}</h2>
            <ul className="mt-10 border-t border-rule">
              {outcomes.map(o => (
                <li key={o.id} className="border-b border-rule py-5 text-[16px] text-ink">
                  <span className="font-serif">&ldquo;{o.result}&rdquo;</span>
                  <span className="text-muted"> - {o.studentLabel}{o.subject ? `, ${o.subject}` : ''}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* ─── FAQ ───────────────────────────────────────────────────────── */}
      <section className="section border-t border-rule">
        <div className="container-content max-w-[72ch]">
          <h2>{c('faq_heading', 'Questions parents ask')}</h2>
          <div className="mt-10">
            {faqItems.map((item, idx) => (
              <details
                key={item.id}
                ref={el => { detailsRefs.current[idx] = el; }}
                onToggle={handleToggle(idx)}
                className="group border-b border-rule [&[open]]:border-l-2 [&[open]]:border-l-ochre [&[open]]:pl-4"
              >
                <summary className="flex items-center justify-between gap-4 py-5 cursor-pointer list-none text-[17px] text-ink font-serif marker:hidden">
                  <span>{item.question}</span>
                  <span className="text-ochre text-[22px] leading-none select-none group-open:rotate-45 transition-transform" aria-hidden="true">+</span>
                </summary>
                <div className="pb-5 pr-2 text-[15px] leading-relaxed text-ink/90">
                  {item.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Final CTA ─────────────────────────────────────────────────── */}
      <section className="bg-navy">
        <div className="container-content py-20 md:py-24 text-center">
          <h2 className="text-cream max-w-[26ch] mx-auto">
            Ready to see if I'm the right tutor for your child?
          </h2>
          <p className="mt-4 text-cream/80 text-body max-w-[52ch] mx-auto">
            A £5 20-minute intro call to see if I'm the right fit. No hard sell.
          </p>
          <div className="mt-8">
            <Link to="/contact" className="btn btn-primary">
              Book a £5 20-minute call
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
